import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Paperclip, Upload, Download, Trash2, X, FileText, Image } from 'lucide-react';
import { useFiles, useUploadFile, useDeleteFile, getFileDownloadUrl } from '../../hooks/useFiles.js';
import { toast } from '../../lib/toast.js';
import type { FileAttachment, FileEntityType } from '@mayksel/types';

const ALLOWED_EXTENSIONS = '.pdf,.png,.jpg,.jpeg,.svg,.xlsx,.docx,.zip';
const MAX_SIZE = 50 * 1024 * 1024;

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImage(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

function FileIcon({ mimeType }: { mimeType: string }) {
  if (isImage(mimeType)) return <Image size={16} />;
  return <FileText size={16} />;
}

interface PreviewModalProps {
  file: FileAttachment;
  onClose: () => void;
}

function PreviewModal({ file, onClose }: PreviewModalProps) {
  const { t } = useTranslation('files');
  const url = getFileDownloadUrl(file.id);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'var(--space-4)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-bg)',
          borderRadius: 'var(--radius-xl)',
          overflow: 'hidden',
          maxWidth: '90vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 400,
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'var(--space-3) var(--space-4)',
          borderBottom: '1px solid var(--color-border)',
        }}>
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
            {file.name}
          </span>
          <button
            onClick={onClose}
            aria-label={t('close_preview')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: 4 }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
          {isImage(file.mimeType) ? (
            <img
              src={url}
              alt={file.name}
              style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', display: 'block' }}
            />
          ) : file.mimeType === 'application/pdf' ? (
            <iframe
              src={url}
              title={file.name}
              style={{ width: '80vw', height: '75vh', border: 'none' }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

interface Props {
  entityType: FileEntityType;
  entityId: string;
}

export default function FileAttachments({ entityType, entityId }: Props) {
  const { t } = useTranslation('files');
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [previewing, setPreviewing] = useState<FileAttachment | null>(null);

  const { data: files = [], isLoading } = useFiles(entityType, entityId);
  const upload = useUploadFile(entityType, entityId);
  const deleteFile = useDeleteFile(entityType, entityId);

  async function handleFiles(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    const file = fileList[0];
    if (!file) return;

    if (file.size > MAX_SIZE) {
      toast.error(t('error.size'));
      return;
    }

    try {
      await upload.mutateAsync(file);
      toast.success(t('toast.uploaded'));
    } catch (_err) {
      toast.error(t('toast.upload_error'));
    }
  }

  async function handleDelete(file: FileAttachment) {
    if (!confirm(t('delete_confirm', { name: file.name }))) return;
    try {
      await deleteFile.mutateAsync(file.id);
      toast.success(t('toast.deleted'));
    } catch (_err) {
      toast.error(t('toast.delete_error'));
    }
  }

  const canPreview = (f: FileAttachment) => isImage(f.mimeType) || f.mimeType === 'application/pdf';

  return (
    <div>
      {/* Upload zone */}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          void handleFiles(e.dataTransfer.files);
        }}
        style={{
          border: `2px dashed ${dragOver ? 'var(--color-brand)' : 'var(--color-border)'}`,
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-6)',
          textAlign: 'center',
          cursor: upload.isPending ? 'default' : 'pointer',
          background: dragOver ? 'color-mix(in srgb, var(--color-brand) 5%, transparent)' : 'var(--color-bg-subtle)',
          transition: 'border-color var(--duration-snap), background var(--duration-snap)',
          marginBottom: 'var(--space-4)',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_EXTENSIONS}
          style={{ display: 'none' }}
          onChange={(e) => void handleFiles(e.target.files)}
        />
        {upload.isPending ? (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{t('uploading')}</p>
        ) : (
          <>
            <Upload size={20} style={{ color: 'var(--color-text-muted)', marginBottom: 'var(--space-2)' }} />
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-1)' }}>
              {dragOver ? t('drop_active') : t('drop_prompt')}
            </p>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{t('format_hint')}</p>
          </>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {[1, 2].map((i) => (
            <div key={i} className="skeleton-line" style={{ height: 44, borderRadius: 'var(--radius-md)' }} />
          ))}
        </div>
      )}

      {/* Empty */}
      {!isLoading && files.length === 0 && (
        <div style={{ textAlign: 'center', padding: 'var(--space-8) 0' }}>
          <Paperclip size={32} style={{ color: 'var(--color-gray-300)', marginBottom: 'var(--space-2)' }} />
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{t('empty_title')}</p>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--space-1)' }}>{t('empty_body')}</p>
        </div>
      )}

      {/* File list */}
      {files.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {files.map((file) => (
            <div
              key={file.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                padding: 'var(--space-2) var(--space-3)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-bg)',
              }}
            >
              <span style={{ color: 'var(--color-text-muted)', flexShrink: 0 }}>
                <FileIcon mimeType={file.mimeType} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{
                  fontSize: 'var(--text-sm)', color: 'var(--color-text)',
                  fontWeight: 'var(--font-medium)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {file.name}
                </p>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                  {formatSize(file.size)}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-1)', flexShrink: 0 }}>
                {canPreview(file) && (
                  <button
                    onClick={() => setPreviewing(file)}
                    aria-label={t('preview')}
                    style={iconBtnStyle}
                  >
                    <FileText size={14} />
                  </button>
                )}
                <a
                  href={getFileDownloadUrl(file.id)}
                  download={file.name}
                  aria-label={t('download')}
                  style={{ ...iconBtnStyle, display: 'flex', alignItems: 'center', textDecoration: 'none' }}
                >
                  <Download size={14} />
                </a>
                <button
                  onClick={() => void handleDelete(file)}
                  aria-label={t('delete')}
                  style={{ ...iconBtnStyle, color: 'var(--color-danger)' }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {previewing && (
        <PreviewModal file={previewing} onClose={() => setPreviewing(null)} />
      )}
    </div>
  );
}

const iconBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28,
  background: 'none', border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)', cursor: 'pointer',
  color: 'var(--color-text-muted)',
};
